import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { GetRequest } from 'components/common';

const DataSourceOAuthContext = createContext(null);

/**
 * DataSourceOAuthProvider - Centralized OAuth handling for data sources
 *
 * This provider:
 * 1. Handles OAuth completion messages in ONE place (no duplicate listeners)
 * 2. Tracks which component initiated the OAuth flow
 * 3. Provides callbacks only to the initiating component
 * 4. Manages shared dataSources state
 *
 * Usage:
 *   const { dataSources, initiateOAuth, refetchDataSources } = useDataSourceOAuth();
 *
 *   initiateOAuth('/authorize-url', 'google_sheets', (result) => {
 *     // Only called for the component that initiated
 *   });
 */
export const DataSourceOAuthProvider = ({ children }) => {
  const [dataSources, setDataSources] = useState([]);
  const [lastSpreadsheetId, setLastSpreadsheetId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Track the pending OAuth callback - only ONE component can be waiting at a time
  const pendingOAuthCallbackRef = useRef(null);
  const pendingOAuthTypeRef = useRef(null);

  // Fetch data sources from the server
  const fetchDataSources = useCallback(() => {
    return new Promise((resolve) => {
      GetRequest({
        url: '/quote-builder/data-sources',
        callback: (response) => {
          const sources = response.data_sources || [];
          setDataSources(sources);
          if (response.last_spreadsheet_id) {
            setLastSpreadsheetId(response.last_spreadsheet_id);
          }
          setLoading(false);
          resolve(sources);
        },
        error: () => {
          setLoading(false);
          resolve([]);
        }
      });
    });
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchDataSources();
  }, [fetchDataSources]);

  // Single OAuth message handler
  useEffect(() => {
    const handleOAuthMessage = (event) => {
      if (event.origin !== window.location.origin) return;

      const oauthTypes = [
        'google_sheets_oauth_complete',
        'microsoft_excel_oauth_complete',
        'airtable_oauth_complete'
      ];

      if (!event.data || !oauthTypes.includes(event.data.type)) return;

      const { type, result } = event.data;

      // Refetch data sources after OAuth
      fetchDataSources().then((sources) => {
        // Only call the pending callback if it matches the OAuth type
        if (pendingOAuthCallbackRef.current &&
            (!pendingOAuthTypeRef.current || pendingOAuthTypeRef.current === type)) {
          const callback = pendingOAuthCallbackRef.current;
          pendingOAuthCallbackRef.current = null;
          pendingOAuthTypeRef.current = null;

          callback({
            success: result?.success || false,
            dataSourceId: result?.dataSourceId,
            oauthType: type,
            dataSources: sources
          });
        }
      });
    };

    window.addEventListener('message', handleOAuthMessage);
    return () => window.removeEventListener('message', handleOAuthMessage);
  }, [fetchDataSources]);

  /**
   * Initiate an OAuth flow
   * @param {string} url - The OAuth authorization URL
   * @param {string} expectedOAuthType - The expected OAuth completion message type (e.g., 'google_sheets_oauth_complete')
   * @param {function} onComplete - Callback when OAuth completes (only called for initiating component)
   */
  const initiateOAuth = useCallback((url, expectedOAuthType, onComplete) => {
    // Register this component as the owner of the OAuth flow
    pendingOAuthCallbackRef.current = onComplete;
    pendingOAuthTypeRef.current = expectedOAuthType;

    // Open OAuth popup
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      url,
      'oauth_popup',
      `width=${width},height=${height},left=${left},top=${top}`
    );

    // Poll for popup close (fallback if message doesn't come through)
    const checkPopup = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkPopup);
        // Refetch data sources even if popup closed without completing OAuth
        fetchDataSources();
      }
    }, 500);

    return popup;
  }, [fetchDataSources]);

  const value = {
    dataSources,
    lastSpreadsheetId,
    loading,
    initiateOAuth,
    refetchDataSources: fetchDataSources
  };

  return (
    <DataSourceOAuthContext.Provider value={value}>
      {children}
    </DataSourceOAuthContext.Provider>
  );
};

/**
 * Hook to access the DataSource OAuth context
 * Must be used within a DataSourceOAuthProvider
 */
export const useDataSourceOAuth = () => {
  const context = useContext(DataSourceOAuthContext);
  if (!context) {
    throw new Error('useDataSourceOAuth must be used within a DataSourceOAuthProvider');
  }
  return context;
};

/**
 * Hook that provides OAuth context if available, or null if not
 * Useful for components that can work with or without the provider
 */
export const useDataSourceOAuthOptional = () => {
  return useContext(DataSourceOAuthContext);
};

export default DataSourceOAuthContext;
